import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { ArrowRight, GitMerge, Eye } from "lucide-react";

/**
 * Audit log of every manual placement move performed by an admin.
 * Route: /admin/placement-moves
 */
export default function AdminPlacementMoves() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/admin/placement-moves")
      .then((r) => { if (alive) setRows(r.data.items || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <DashboardLayout title="Placement Moves" subtitle={`${rows.length} audit-logged move${rows.length === 1 ? "" : "s"}`}>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 text-sm text-navy/40">Loading moves…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <GitMerge className="mx-auto text-navy/30" size={36} />
            <div className="mt-3 font-serif text-lg text-navy">No manual moves yet</div>
            <p className="text-xs text-navy/50 mt-1">Every manual placement change from any admin will appear here.</p>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[1000px]" data-testid="placement-moves-table">
            <thead className="bg-navy text-white">
              <tr>
                {["Member", "From", "→", "To", "Reason", "Admin", "When", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`placement-move-${r.id}`} className="border-b border-border last:border-0 hover:bg-cream/40">
                  <td className="px-4 py-3">
                    <div className="font-medium text-navy">{r.user_name || "—"}</div>
                    <div className="text-[11px] font-mono text-navy/50">{r.user_code}</div>
                  </td>
                  <td className="px-4 py-3 text-navy/60">
                    <div className="font-mono">{r.old_parent_code || "—"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-navy/40">{r.old_side === "L" ? "Left" : r.old_side === "R" ? "Right" : "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-gold-deep"><ArrowRight size={14} /></td>
                  <td className="px-4 py-3 text-navy">
                    <div className="font-mono">{r.new_parent_code}</div>
                    <div className="text-[10px] uppercase tracking-widest text-olive">{r.new_side === "L" ? "Left" : "Right"}</div>
                  </td>
                  <td className="px-4 py-3 text-navy/60 max-w-xs truncate" title={r.reason || ""}>{r.reason || "—"}</td>
                  <td className="px-4 py-3 text-navy/70">
                    <div className="text-xs">{r.actor_name || "—"}</div>
                    <div className="text-[10px] font-mono text-navy/40">{r.actor_user_code}</div>
                  </td>
                  <td className="px-4 py-3 text-navy/50 text-xs">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/members/${r.user_code}`}
                      data-testid={`placement-view-${r.id}`}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border border-gold/30 text-gold-deep hover:bg-gold/10 rounded-sm"
                    >
                      <Eye size={11} /> View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}
